/* eslint-disable import/no-anonymous-default-export */
import { apiClient } from "../lib/axiosConfig";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const buildLoginRedirectUrl = (redirectPath: string): string =>
  `/login?redirect=${encodeURIComponent(redirectPath)}`;
const PLAIN_LOGIN_REDIRECT_KEY = "plainLoginRedirect";
const isForcedLogout = (): boolean =>
  sessionStorage.getItem("logoutInProgress") === "1";
const shouldUsePlainLoginRedirect = (message: unknown): boolean => {
  const normalizedMessage = String(message || "").toLowerCase();
  return (
    isForcedLogout() ||
    normalizedMessage.includes("another device") ||
    normalizedMessage.includes("single device limit") ||
    normalizedMessage.includes("device limit")
  );
};

// ===== TOKEN HELPERS =====
export const getToken = (): string | null => {
  return (
    sessionStorage.getItem("token") || localStorage.getItem("token") || null
  );
};

export const sanitizePlayableUrl = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const unquoted = raw.replace(/^"+/, "").replace(/"+$/, "").trim();

  const localApiOrigin = (() => {
    try {
      return API_BASE_URL ? new URL(API_BASE_URL).origin : "";
    } catch {
      return "";
    }
  })();

  const proxyMarkers = [
    "/api/proxy-cf/",
    "/api/proxy-s3/",
    "/api/proxy-ext-cf/",
  ];

  const repairNestedLocalProxyUrl = (input: string): string | null => {
    for (const marker of proxyMarkers) {
      const firstIndex = input.indexOf(marker);
      const lastIndex = input.lastIndexOf(marker);
      if (firstIndex !== -1 && lastIndex !== firstIndex) {
        const tail = input.slice(lastIndex + marker.length).replace(/^\/+/, "");
        if (input.startsWith("http://") || input.startsWith("https://")) {
          try {
            const url = new URL(input);
            if (localApiOrigin && url.origin === localApiOrigin) {
              return `${url.origin}${marker}${tail}${url.search}`;
            }
          } catch {}
        }

        if (input.startsWith("/")) {
          return `${marker}${tail}`;
        }
      }
    }
    return null;
  };

  try {
    const url = new URL(unquoted);
    const repaired = repairNestedLocalProxyUrl(url.toString());
    if (repaired) {
      return repaired;
    }
  } catch {}

  return repairNestedLocalProxyUrl(unquoted) || unquoted;
};

export const setToken = (token: string): void => {
  sessionStorage.setItem("token", token);
  localStorage.removeItem("token");
};

export const removeToken = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("authToken");
  sessionStorage.removeItem("token");
};

// ===== TYPES (unchanged) =====
export interface Class {
  _id: string;
  title: string;
  order: number;
  slug?: string;
}

export interface Subject {
  _id: string;
  title: string;
  order: number;
  slug?: string;
  course_ids?: string[];
  class_ids?: string[];
}

export interface Chapter {
  chapter_order: number;
  index: any;
  _id: string;
  title: string;
  order: number;
  slug?: string;
  course_ids: string[];
  class_ids: string[];
  subject_ids: string[];
}

export interface Video {
  video_url: string;
  url: any;
  stream_url: any;
  _id: string;
  title: string;
  slug: string;
  video_duration: string;
  hls_playlist_url: string;
  thumbnail_list: any[];
  new_thumbnail_images: any;
  description?: string;
  episode_index?: number;
  progress?: number;
  is_user_subscribed: boolean;
  is_subscription: boolean;
  monetization: number;
  seriesSlug?: string;
  is_locked?: boolean;
  is_free_preview?: boolean;
}

export interface SearchVideoResult {
  _id: string;
  id: string;
  title: string;
  slug: string;
  description?: string;
  video_duration?: string;
  hls_playlist_url?: string | null;
  video_url?: string | null;
  url?: string | null;
  stream_url?: string | null;
  thumbnail_image?: string | null;
  new_thumbnail_images?: any;
  thumbnail_list?: any[];
  episode_index?: number;
  order?: number;
  rank?: string;
  monetization?: number;
  is_locked?: boolean;
  is_free_preview?: boolean;
  master_details_id?: string | null;
  series?: {
    _id: string;
    title: string;
    slug: string;
  } | null;
  chapter?: {
    _id: string;
    title: string;
    slug: string;
  } | null;
  subject?: {
    _id: string;
    title: string;
    slug: string;
  } | null;
  season?: {
    _id: string;
    title: string;
    season?: number;
  } | null;
  studio_name?: string | null;
  studio_slug?: string | null;
}

export interface ContinueWatchingItem {
  is_free_preview: boolean;
  hls_playlist_url: any;
  url: any;
  description: string;
  last_watched: number;
  _id: string;
  media_id: string;
  profile_id: string;
  title: string;
  slug: string;
  video_count?: number;
  video_duration: number;
  watched_duration: number;
  progress: number;
  thumbnail: string | null;
  thumbnail_image?: string | null;
  thumbnail_webp: string | null;
  season_details?: {
    _id: string;
    title: string;
    season_index: number;
    is_active: boolean;
    is_archived: boolean;
  };
  chapter_details?: {
    _id: string;
    title: string;
    video_count?: number;
  };
  master_details_id?: {
    description: any;
    _id: string;
    title: string;
    monetization: number;
    studio_name: string;
    studio_slug: string;
    status: number;
    is_active: number;
    is_archived: number;
    scheduled: boolean;
    relieve_date?: string;
    thumbnail_list?: any[];
    new_thumbnail_images?: {
      default?: { to: string; tp?: string };
      webp?: { to: string; tp?: string };
    };
    chapter_details?: {
      _id: string;
      title: string;
      video_count?: number;
    };
    subject_details?: {
      _id: string;
      title: string;
    };
    season_details?: {
      _id: string;
      title: string;
      season_index?: number;
      is_active?: boolean;
      is_archived?: boolean;
    };
    chapter?: {
      _id: string;
      title: string;
      video_count?: number;
    };
    chapter_name?: string;
    subject?: {
      _id: string;
      title: string;
    };
    season?: {
      _id: string;
      title: string;
      season?: number;
    };
    season_name?: string;
    provider_name?: string;
    video_count?: number;
  };
  studio_name?: string;
  studio_slug?: string;
  provider_name?: string;
  chapter?: {
    _id: string;
    title: string;
    slug?: string;
    video_count?: number;
  } | null;
  chapter_name?: string;
  season?: {
    _id: string;
    title: string;
    season?: number;
  } | null;
  season_name?: string;
  monetization?: number;
  is_latest_watch?: boolean;
  is_allowed?: number;
  updated_at: string;
  created_at: string;
}

export interface WatchHistoryResponse {
  statusCode: number;
  message: string;
  status: string;
  response: {
    data: ContinueWatchingItem[];
    nextPage: number | null;
    totalPages: number;
    recordsPerPage: number;
    total: number;
  };
  error: boolean;
}

export interface UpdateProgressRequest {
  profile_id: string;
  media_id: string;
  watched_duration: number;
  video_duration: number;
}
export interface Channel {
  totalVideos: number;
  _id: string;
  title: string;
  data: Video[];
  count: number;
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface CourseWithDetails {
  _id: string;
  title: string;
  order: number;
  profile_picture?: string;
  description?: string;
  slug?: string;
  class_details?: {
    _id: string;
    title: string;
    order: number;
    slug?: string;
  };
  subjectTitle?: {
    _id: string;
    title: string;
    order: number;
    slug?: string;
  };
  master_id?: string | null;
  episode_count: number;
  video_count?: number;
  tests_count?: number;
  is_test_series?: boolean;
  is_static_test?: boolean;
  static_test_count?: number;
  total_duration?: number;
  languages?: string[];
  first_video?: {
    _id: string;
    title: string;
    duration: number;
    thumbnail_image?: string;
  };
}

export interface StreamCollection {
  _id: string;
  title: string;
  order: number;
  profile_picture?: string;
  type: number;
  row_count: number;
  data: CourseWithDetails[];
  count: number;
  total: number;
  totalVideos: number;
  recordsPerPage: number;
  currentPage: number;
  totalPages: number;
}

export interface ChannelVideosResponse {
  statusCode: number;
  message: string;
  status: string;
  response: {
    data: Channel[];
    total: number;
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    next: number | null;
    previous: number | null;
  };
  error: boolean;
}

export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  status: string;
  response: T;
  error: boolean;
}

export interface UserProfile {
  last_name: any;
  id: string;
  _id?: string;
  first_name: string;
  profile_picture: string | null;
  is_default_profile: boolean;
  is_pin_lock_enable: boolean;
  is_active?: boolean;
  is_archived?: boolean;
}

export interface UserData {
  user: {
    user_type: string;
    gender: any;
    dob: any;
    class: any;
    section: any;
    preparing_for: any;
    school_name: any;
    school_code: any;
    username: any;
    name: string;
    profile_picture: string;
    profiles: any;
    _id: string;
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    profilePicture: string | null;
    admissionNumber: string;
    loginCount?: number;
    walkthrough_shown?: boolean;
    walkthrough_shown_at?: string | null;
  };
  profiles: UserProfile[];
  activeProfileId: string | null;
}

export interface UserStats {
  name: string;
  className: string;
  streak: number;
  xp: number;
  badgesEarned: number;
}

export const createLead = (data: {
  name: string;
  phone: string;
  source: string;
}) => apiClient.post("/leads/create", data);

export const fetchUserProfileOnly = async (): Promise<any> => {
  const response = await apiClient.get("/auth/profile");
  return response.data.user || response.data.data || response.data;
};

export const fetchProfilesListOnly = async (): Promise<UserProfile[]> => {
  const response = await apiClient.get("/profile/list");
  return response.data.data?.data || response.data.data || [];
};

export const fetchUserWithProfiles = async (): Promise<UserData> => {
  const response = await apiClient.get("/auth/me-with-profiles");
  return response.data.data;
};

export const fetchUserStats = async (): Promise<UserStats> => {
  const response = await apiClient.get("/auth/user-stats");
  return response.data.data;
};

export const setActiveProfile = async (profileId: string): Promise<void> => {
  await apiClient.post(`/profiles/active/${profileId}`);
};

export const fetchActiveProfile = async (): Promise<UserProfile | null> => {
  try {
    const response = await apiClient.get("/profiles/active");
    return response.data.data;
  } catch {
    return null;
  }
};

export const listProfiles = async (): Promise<UserProfile[]> => {
  const response = await apiClient.get("/profiles/list");
  return response.data.data;
};

export const fetchUserProfiles = async () => {
  try {
    const response = await apiClient.get("/profile/list");
    return response.data.data || [];
  } catch (error) {
    console.error("Error fetching profiles:", error);
    return [];
  }
};

export const getUserProfile = async (): Promise<any> => {
  try {
    const response = await apiClient.get("/profile/active");
    return response.data.response || response.data.data || response.data;
  } catch {
    return null;
  }
};

export const fetchHomeData = async (): Promise<StreamCollection[]> => {
  const response = await apiClient.get("/home/get-home-data?limit=10", {
    headers: { "x-language-code": "en" },
  });
  if (response.data.statusCode === 200 && !response.data.error) {
    return response.data.response.data || [];
  }
  throw new Error(response.data.message || "Failed to fetch home data");
};

export const fetchClasses = async (
  courseId: string,
  page = 1,
  limit = 15,
): Promise<Class[]> => {
  const response = await apiClient.get(
    `/home/classes?courseId=${courseId}&page=${page}&limit=${limit}&is_pagination=true`,
    { headers: { "x-language-code": "en" } },
  );
  if (response.data.statusCode === 200 && !response.data.error) {
    return response.data.response.data || [];
  }
  throw new Error(response.data.message || "Failed to fetch classes");
};

export const fetchSubjects = async (
  courseId: string,
  classId: string,
  page = 1,
  limit = 25,
): Promise<Subject[]> => {
  const response = await apiClient.get(
    `/home/subjects?courseId=${courseId}&classId=${classId}&page=${page}&limit=${limit}&is_pagination=true`,
    { headers: { "x-language-code": "en" } },
  );
  if (response.data.statusCode === 200 && !response.data.error) {
    return response.data.response.data || [];
  }
  throw new Error(response.data.message || "Failed to fetch subjects");
};

export const fetchChapters = async (
  courseId: string,
  subjectId: string,
  classId: string,
): Promise<Chapter[]> => {
  const response = await apiClient.get(
    `/home/all/chapterlist?courseId=${courseId}&subjectId=${subjectId}&classId=${classId}`,
    { headers: { "x-language-code": "en" } },
  );
  if (response.data.statusCode === 200 && !response.data.error) {
    return response.data.response.chapterList || [];
  }
  throw new Error(response.data.message || "Failed to fetch chapters");
};

export const fetchChaptersForClass = async (
  courseId: string,
  classId: string,
): Promise<Chapter[]> => {
  const response = await apiClient.get(
    `/home/all/chapterlist?courseId=${courseId}&classId=${classId}`,
    { headers: { "x-language-code": "en" } },
  );
  if (response.data.statusCode === 200 && !response.data.error) {
    return response.data.response.chapterList || [];
  }
  throw new Error(response.data.message || "Failed to fetch chapters");
};

// Providers per request: enough that a chapter loads in one call (callers still page on totalPages).
const PROVIDERS_PER_REQUEST = 100;

export const fetchVideos = async (
  courseId: string,
  subjectId: string,
  classId: string,
  chapterId: string,
  providerPage = 1,
  insideLimit = 500,
): Promise<{ channels: Channel[]; total: number; totalPages: number }> => {
  const response = await apiClient.get(
    `/home/channel-list?courseId=${courseId}&subjectId=${subjectId}&classId=${classId}&chapterId=${chapterId}&limit=${PROVIDERS_PER_REQUEST}&inside_limit=${insideLimit}&page=${providerPage}`,
    { headers: { "x-language-code": "en" } },
  );
  if (response.data.statusCode === 200 && !response.data.error) {
    let channels = response.data.response.data || [];
    // Sort videos within each channel
    channels = channels.map((channel: any) => ({
      ...channel,
      data: (channel.data || []).sort((a: any, b: any) => {
        const getSortValue = (video: any) => {
          if (video.episode_index !== undefined && video.episode_index !== null)
            return video.episode_index;
          if (video.order !== undefined && video.order !== null)
            return video.order;
          if (video.sort_order !== undefined && video.sort_order !== null)
            return video.sort_order;
          if (video.rank) {
            const match = video.rank.match(/\d+/);
            if (match) return parseInt(match[0], 10);
          }
          if (video.created_at) return new Date(video.created_at).getTime();
          return video.title || "";
        };
        const valA = getSortValue(a);
        const valB = getSortValue(b);
        if (typeof valA === "number" && typeof valB === "number") {
          return valA - valB;
        }
        return String(valA).localeCompare(String(valB));
      }),
    }));
    return {
      channels,
      total: response.data.response.total || 0,
      totalPages: response.data.response.totalPages || 1,
    };
  }
  throw new Error(response.data.message || "Failed to fetch videos");
};

export const searchVideos = async (
  query: string,
  options?: {
    page?: number;
    limit?: number;
    courseId?: string | null;
    classId?: string | null;
    subjectId?: string | null;
    chapterId?: string | null;
    masterDetailsId?: string | null;
    seasonId?: string | null;
  },
): Promise<{
  data: SearchVideoResult[];
  total: number;
  totalPages: number;
}> => {
  const params = new URLSearchParams();
  params.set("q", query);
  params.set("page", String(options?.page || 1));
  params.set("limit", String(options?.limit || 20));

  if (options?.courseId) params.set("courseId", options.courseId);
  if (options?.classId) params.set("classId", options.classId);
  if (options?.subjectId) params.set("subjectId", options.subjectId);
  if (options?.chapterId) params.set("chapterId", options.chapterId);
  if (options?.masterDetailsId)
    params.set("masterDetailsId", options.masterDetailsId);
  if (options?.seasonId) params.set("seasonId", options.seasonId);

  const response = await apiClient.get(
    `/home/search-videos?${params.toString()}`,
    {
      headers: { "x-language-code": "en" },
    },
  );
  if (response.data.statusCode === 200 && !response.data.error) {
    return {
      data: response.data.response.data || [],
      total: response.data.response.total || 0,
      totalPages: response.data.response.totalPages || 1,
    };
  }
  throw new Error(response.data.message || "Failed to search videos");
};

export const fetchTotalProviders = async (
  courseId: string,
  subjectId: string,
  classId: string,
  chapterId: string,
): Promise<number> => {
  const response = await apiClient.get(
    `/home/channel-list?courseId=${courseId}&subjectId=${subjectId}&classId=${classId}&chapterId=${chapterId}&limit=1&page=1&inside_limit=1`,
    { headers: { "x-language-code": "en" } },
  );
  if (response.data.statusCode === 200 && !response.data.error) {
    return response.data.response.total || 0;
  }
  return 0;
};

export const fetchAllVideosByChapter = async (
  courseId: string,
  subjectId: string,
  classId: string,
  chapterId: string,
  insideLimit = 12,
  onProgress?: (loaded: number, total: number) => void,
): Promise<Channel[]> => {
  const firstResponse = await apiClient.get(
    `/home/channel-list?courseId=${courseId}&subjectId=${subjectId}&classId=${classId}&chapterId=${chapterId}&limit=1&inside_limit=${insideLimit}&page=1`,
    { headers: { "x-language-code": "en" } },
  );
  const firstData = firstResponse.data;
  if (firstData.statusCode !== 200 || firstData.error) {
    throw new Error(firstData.message || "Failed to fetch videos");
  }
  const totalPages = firstData.response.totalPages || 1;
  if (onProgress) onProgress(1, totalPages);

  const remainingPromises = Array.from({ length: totalPages - 1 }, (_, i) =>
    apiClient.get(
      `/home/channel-list?courseId=${courseId}&subjectId=${subjectId}&classId=${classId}&chapterId=${chapterId}&limit=1&inside_limit=${insideLimit}&page=${i + 2}`,
      { headers: { "x-language-code": "en" } },
    ),
  );
  const remainingResponses = await Promise.all(remainingPromises);
  let allChannels = [...firstData.response.data];
  remainingResponses.forEach((resp, idx) => {
    if (resp.data.statusCode === 200 && !resp.data.error) {
      allChannels = [...allChannels, ...resp.data.response.data];
    }
    if (onProgress) onProgress(idx + 2, totalPages);
  });
  return allChannels;
};

export const fetchSubscriptionPlans = async () => {
  const response = await apiClient.get("/subscriptions");
  if (response.data.statusCode === 200) {
    const subscriptions =
      response.data.response?.subscriptionList?.subscriptions || [];
    const userSubscription =
      response.data.response?.subscriptionList?.user_subscription || {};
    const isSubscribed =
      userSubscription?.is_user_subscribed === true ||
      userSubscription?.is_user_subscribed === 1;
    const transformedSubscriptions = subscriptions.map((sub: any) => ({
      _id: sub._id,
      name: sub.name,
      price: sub.prices?.amount || 0,
      discounted_price: sub.prices?.discounted_price || 0,
      discounted_percentage: sub.prices?.discounted_percentage || 0,
      currency: sub.prices?.currency || "INR",
      currency_sign: sub.prices?.currency_sign || "₹",
      description: sub.description,
      days: sub.days,
      features: [
        "Access to all courses",
        "Unlimited video streaming",
        "Self-paced Learning",
        "Practice tests included",
        "4 Content Partners: Ekluvya + AhaGuru + T.I.M.E + Tutorix",
      ],
      popular: sub.name?.toLowerCase().includes("pre-launch") || false,
      badge: sub.name?.toLowerCase().includes("pre-launch")
        ? "Limited Time Offer"
        : "",
    }));
    return {
      subscriptions: transformedSubscriptions,
      isSubscribed,
      userSubscription,
      payment_type:
        response.data.response?.subscriptionList?.payment_type || "3",
    };
  }
  throw new Error(
    response.data.message || "Failed to fetch subscription plans",
  );
};

type SubscriptionStatus = { is_subscribed: boolean; active_subscription: any };

const requestUserSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  try {
    const response = await apiClient.get("/subscriptions");
    if (response.data.statusCode === 200) {
      const userSubscription =
        response.data.response?.subscriptionList?.user_subscription || {};
      const isSubscribed =
        userSubscription?.is_user_subscribed === true ||
        userSubscription?.is_user_subscribed === 1;
      return {
        is_subscribed: isSubscribed,
        active_subscription: userSubscription?.active_subscription || null,
      };
    }
    return { is_subscribed: false, active_subscription: null };
  } catch {
    return { is_subscribed: false, active_subscription: null };
  }
};

// Callers that overlap (the header and the page on load) share one request per token.
// Nothing is kept once it settles, so a new subscription is never hidden by an old answer.
let subscriptionStatusInFlight: {
  token: string | null;
  promise: Promise<SubscriptionStatus>;
} | null = null;

export const getUserSubscriptionStatus = async () => {
  const token = getToken();
  if (subscriptionStatusInFlight?.token === token) {
    return subscriptionStatusInFlight.promise;
  }
  const entry = { token, promise: requestUserSubscriptionStatus() };
  subscriptionStatusInFlight = entry;
  entry.promise.finally(() => {
    if (subscriptionStatusInFlight === entry) subscriptionStatusInFlight = null;
  });
  return entry.promise;
};

export const validateSubscriptionCoupon = async (
  subscriptionId: string,
  couponCode: string,
) => {
  const response = await apiClient.get(
    `/coupons?subscription_id=${subscriptionId}`,
  );
  if (response.data.statusCode === 200) {
    return response.data.response;
  }
  throw new Error(response.data.message || "Invalid coupon code");
};

export const fetchContinueWatching = async (
  profileId: string,
  limit?: number,
  isPromo: boolean = true,
): Promise<WatchHistoryResponse> => {
  let url = `/watch-history?watch_type=continue_watch&is_promo=${isPromo}&profile_id=${profileId}&_ts=${Date.now()}`;
  if (limit) url += `&limit=${limit}`;
  const response = await apiClient.get(url);
  return response.data;
};

export const updateWatchProgress = async (
  profileId: string,
  mediaId: string,
  watchedDuration: number,
  videoDuration: number,
  playTime: number = 0,
  title?: string,
): Promise<any> => {
  const response = await apiClient.post("/watch-history/progress", {
    profile_id: profileId,
    media_id: mediaId,
    watched_duration: Math.floor(Math.max(0, watchedDuration)),
    video_duration: Math.floor(Math.max(0, videoDuration)),
    play_time: Math.floor(playTime),
    title: title || "Untitled Video",
  });
  return response.data;
};

export const deleteWatchHistory = async (
  profileId: string,
  mediaId: string,
  watchType: string = "continue_watch",
): Promise<any> => {
  const response = await apiClient.delete(
    `/watch-history/${mediaId}?watch_type=${watchType}`,
    {
      data: { profile_id: profileId },
    },
  );
  return response.data;
};

export const clearAllWatchHistory = async (
  profileId: string,
  watchType: string = "continue_watch",
): Promise<any> => {
  const response = await apiClient.delete(
    `/watch-history/delete/all?watch_type=${watchType}`,
    {
      data: { profile_id: profileId },
    },
  );
  return response.data;
};

export const getVideoUrl = async (mediaId: string): Promise<string | null> => {
  try {
    const token = getToken();
    const streamUrl = `${API_BASE_URL}/videos/stream/${mediaId}`;
    const response = await fetch(streamUrl, {
      method: "GET",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include",
      redirect: "follow",
    });

    if (response.status === 401) {
      let responseMessage = "";
      try {
        const data = await response.clone().json();
        responseMessage = data?.message || "";
      } catch {
        responseMessage = "";
      }
      // Manually trigger logout – the interceptor won't catch fetch
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("redirectAfterLogin");
      sessionStorage.removeItem("loginRedirectTarget");
      sessionStorage.removeItem("redirectAfterSubscription");
      sessionStorage.removeItem("authRedirecting");
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      if (shouldUsePlainLoginRedirect(responseMessage)) {
        sessionStorage.setItem(PLAIN_LOGIN_REDIRECT_KEY, "1");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return null;
      }
      sessionStorage.removeItem(PLAIN_LOGIN_REDIRECT_KEY);
      const redirectAfterLogin = window.location.pathname.startsWith(
        "/home/courses",
      )
        ? "/subscription"
        : window.location.pathname;
      sessionStorage.setItem("redirectAfterLogin", redirectAfterLogin);
      if (window.location.pathname !== "/login") {
        window.location.href = buildLoginRedirectUrl(redirectAfterLogin);
      }
      return null;
    }

    if (response.ok) {
      const finalUrl = response.url;
      if (finalUrl !== streamUrl) {
        return sanitizePlayableUrl(finalUrl);
      }
      const data = await response.json();
      return (
        sanitizePlayableUrl(data.url) ||
        sanitizePlayableUrl(data.hls_playlist_url) ||
        null
      );
    }
    return null;
  } catch (error) {
    console.error("Error getting video URL:", error);
    return null;
  }
};

// ===== DEFAULT EXPORT =====
export default {
  API_BASE_URL,
  getToken,
  setToken,
  removeToken,
  fetchHomeData,
  fetchClasses,
  fetchSubjects,
  fetchChapters,
  fetchVideos,
  searchVideos,
  fetchAllVideosByChapter,
  fetchTotalProviders,
  fetchSubscriptionPlans,
  validateSubscriptionCoupon,
  getUserSubscriptionStatus,
  fetchContinueWatching,
  updateWatchProgress,
  deleteWatchHistory,
  clearAllWatchHistory,
  getVideoUrl,
  fetchUserProfileOnly,
  fetchProfilesListOnly,
  fetchUserWithProfiles,
  fetchUserStats,
  setActiveProfile,
  fetchActiveProfile,
  listProfiles,
  fetchUserProfiles,
  getUserProfile,
  createLead,
};
